<template>
  <div class="customer-signin-page-bg">
    <header class="signin-header">
      <div class="header-content">
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Tartoro Logo" class="header-logo" />
          <span class="header-title">Tartoro</span>
        </div>
        <div class="language-dropdown" @click="toggleDropdown">
          <span class="globe-icon">🌐</span>
          <span id="selected-language">{{ currentLanguage }}</span>
          <span class="chevron-down">▼</span>
          <div class="dropdown-content" v-show="isDropdownOpen">
            <a href="#" @click.prevent="changeLanguage('en')">English</a>
            <a href="#" @click.prevent="changeLanguage('es')">Español</a>
          </div>
        </div>
      </div>
    </header>
    <div class="customer-signin-container">
      <!-- Left Side: Welcome Section -->
      <div class="signin-left">
        <div class="signin-overlay"></div>
        <div class="signin-welcome">
          <div class="signin-icon">
            <i class="fas fa-key"></i>
          </div>
          <h2>{{ $t('forgotPassword.welcome.title') }}</h2>
          <p class="signin-subtitle">{{ $t('forgotPassword.welcome.description') }}</p>
          <div class="signin-features-card">
            <ul class="signin-features">
              <li><i class="fas fa-check-circle"></i> {{ $t('customerSignIn.welcome.features.secure') }}</li>
              <li><i class="fas fa-check-circle"></i> {{ $t('customerSignIn.welcome.features.easy') }}</li>
              <li><i class="fas fa-check-circle"></i> {{ $t('customerSignIn.welcome.features.support') }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Right Side: Form Section -->
      <div class="signin-right">
        <div class="signin-box">
          <div class="back-button-container">
            <BackButton @back="$router.push('/customer-signin')">{{ $t('common.back') }}</BackButton>
          </div>
          <!-- Title for Email Input Section -->
          <h1 v-if="!otpSent">{{ $t('forgotPassword.welcome.title') }}</h1>
          
          <!-- Email Input Section -->
          <div v-if="!otpSent" class="email-section">
            <form @submit.prevent="handleEmailSubmit" class="signin-form">
              <div class="form-group">
                <label for="email">{{ $t('forgotPassword.form.email.label') }}</label>
                <div class="input-icon-group">
                  <i class="fas fa-envelope"></i>
                  <input
                    type="email"
                    id="email"
                    v-model="email"
                    required
                    :placeholder="$t('forgotPassword.form.email.placeholder')"
                  />
                </div>
              </div>
              <div v-if="error" class="error-message">{{ error }}</div>
              <button type="submit" class="signin-button" :disabled="loading">
                {{ loading ? $t('forgotPassword.form.loading') : $t('forgotPassword.form.requestOTP') }}
              </button>
            </form>
          </div>

          <!-- OTP Verification Section -->
          <div v-else-if="!showPasswordChange" class="otp-section">
            <h1>{{ $t('otpVerification.title') }}</h1>
            <p class="otp-info">{{ $t('otpVerification.description') }}</p>
            <form @submit.prevent="verifyOTP" class="signin-form">
              <div class="form-group" style="display: flex; flex-direction: column; align-items: center; width: 100%;">
                <label for="otp" class="otp-label" style="align-self: flex-start; margin-bottom: 0.5rem;">{{ $t('forgotPassword.form.otp.label') }}</label>
                <div class="otp-inputs">
                  <input v-for="(digit, idx) in otp"
                         :key="idx"
                         type="text"
                         maxlength="1"
                         class="otp-input"
                         v-model="otp[idx]"
                         @input="onInput(idx, $event)"
                         @keydown.backspace="onBackspace(idx)"
                         :ref="`otp${idx}`"
                  />
                </div>
              </div>
              <div v-if="error" class="error-message">{{ error }}</div>
              <button type="submit" class="signin-button" :disabled="loading || !isOtpComplete">
                {{ loading ? $t('forgotPassword.form.loading') : $t('forgotPassword.form.verifyOTP') }}
              </button>
              <div class="otp-resend">
                {{ $t('forgotPassword.form.resend') }}
                <a href="#" @click.prevent="handleResend" :style="{pointerEvents: countdown > 0 ? 'none' : 'auto', opacity: countdown > 0 ? 0.5 : 1}">
                  {{ resendLoading ? $t('forgotPassword.form.resending') : (countdown > 0 ? countdown : $t('forgotPassword.form.resendLink')) }}
                </a>
              </div>
            </form>
          </div>

          <!-- Password Change Section -->
          <div v-else class="password-change-section">
            <form @submit.prevent="handlePasswordChange" class="signin-form">
              <div class="form-group">
                <label for="newPassword">{{ $t('changePassword.form.newPassword.label') }}</label>
                <div class="input-icon-group">
                  <i class="fas fa-lock"></i>
                  <input
                    :type="showNewPassword ? 'text' : 'password'"
                    id="newPassword"
                    v-model="newPassword"
                    required
                    :placeholder="$t('changePassword.form.newPassword.placeholder')"
                  />
                  <i 
                    :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" 
                    @click="showNewPassword = !showNewPassword"
                    class="password-toggle"
                  ></i>
                </div>
              </div>

              <div class="form-group">
                <label for="confirmPassword">{{ $t('changePassword.form.confirmPassword.label') }}</label>
                <div class="input-icon-group">
                  <i class="fas fa-lock"></i>
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    id="confirmPassword"
                    v-model="confirmPassword"
                    required
                    :placeholder="$t('changePassword.form.confirmPassword.placeholder')"
                  />
                  <i 
                    :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" 
                    @click="showConfirmPassword = !showConfirmPassword"
                    class="password-toggle"
                  ></i>
                </div>
              </div>

              <div v-if="error" class="error-message">{{ error }}</div>
              <button type="submit" class="signin-button" :disabled="loading">
                {{ loading ? $t('changePassword.form.loading') : $t('changePassword.form.submit') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/services/authService';
import '@/styles/CustomerSignIn.css';
import '@/styles/OtpVerification.css';
import BackButton from '@/components/BackButton.vue';

export default {
  name: 'CustomerForgotPassword',
  components: {
    BackButton
  },
  data() {
    return {
      email: '',
      otp: ['', '', '', ''],
      newPassword: '',
      confirmPassword: '',
      showNewPassword: false,
      showConfirmPassword: false,
      otpSent: false,
      showPasswordChange: false,
      error: '',
      loading: false,
      resendLoading: false,
      countdown: 0,
      isDropdownOpen: false,
      currentLanguage: 'English'
    }
  },
  computed: {
    isOtpComplete() {
      return this.otp.every(d => d && d.length === 1 && /[0-9]/.test(d));
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    changeLanguage(lang) {
      this.currentLanguage = lang === 'en' ? 'English' : 'Español';
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.isDropdownOpen = false;
    },
    onInput(idx, event) {
      const value = event.target.value.replace(/[^0-9]/g, '');
      this.otp[idx] = value;
      if (value && idx < 3) {
        this.$nextTick(() => {
          this.$refs[`otp${idx+1}`][0].focus();
        });
      }
    },
    onBackspace(idx) {
      if (!this.otp[idx] && idx > 0) {
        this.$refs[`otp${idx-1}`][0].focus();
      }
    },
    async handleEmailSubmit() {
      this.error = '';
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.error = this.$t('forgotPassword.errors.invalidEmail');
        return;
      }

      this.loading = true;
      try {
        // Send OTP directly - the backend will handle email validation
        const response = await authService.requestPasswordResetOTP(this.email);
        if (response.success) {
          this.otpSent = true;
          localStorage.setItem('resetEmail', this.email);
          this.startCountdown();
          // Clear any previous errors
          this.error = '';
        } else {
          throw new Error(response.message || this.$t('forgotPassword.errors.requestFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('forgotPassword.errors.requestFailed');
        // Reset OTP sent state if there's an error
        this.otpSent = false;
      } finally {
        this.loading = false;
      }
    },
    async verifyOTP() {
      this.error = '';
      if (!this.isOtpComplete) {
        this.error = this.$t('forgotPassword.errors.incompleteOTP');
        return;
      }
      this.loading = true;
      try {
        const response = await authService.verifyResetOTP({
          email: this.email,
          otp: this.otp.join('')
        });
        if (response.success) {
          this.showPasswordChange = true;
        } else {
          throw new Error(response.message || this.$t('forgotPassword.errors.verificationFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('forgotPassword.errors.verificationFailed');
      } finally {
        this.loading = false;
      }
    },
    async handleResend() {
      if (this.countdown > 0) return;
      this.error = '';
      this.resendLoading = true;
      try {
        const response = await authService.resendOTP(this.email);
        if (response.success) {
          this.startCountdown();
        } else {
          throw new Error(response.message || this.$t('forgotPassword.errors.resendFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('forgotPassword.errors.resendFailed');
      } finally {
        this.resendLoading = false;
      }
    },
    async handlePasswordChange() {
      this.error = '';
      
      if (this.newPassword !== this.confirmPassword) {
        this.error = this.$t('changePassword.errors.passwordMismatch');
        return;
      }

      if (this.newPassword.length < 8) {
        this.error = this.$t('changePassword.errors.passwordTooShort');
        return;
      }

      this.loading = true;
      try {
        const response = await authService.resetPassword({
          email: this.email,
          newPassword: this.newPassword
        });

        if (response.success) {
          localStorage.removeItem('resetEmail');
          this.$router.push('/customer-signin');
        } else {
          throw new Error(response.message || this.$t('changePassword.errors.resetFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('changePassword.errors.resetFailed');
      } finally {
        this.loading = false;
      }
    },
    startCountdown() {
      this.countdown = 60;
      const timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    }
  },
  created() {
    const savedLanguage = localStorage.getItem('language') || 'en';
    this.currentLanguage = savedLanguage === 'en' ? 'English' : 'Español';
    this.$i18n.locale = savedLanguage;
  }
}
</script>

<style scoped>
.back-button-container {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0;
  margin-top: 0;
  position: absolute;
  top: -1rem;
  left: 1rem;
}

.signin-box {
  position: relative;
  padding-top: 2rem;
}

.password-toggle {
  position: absolute;
  right: 12px;
  cursor: pointer;
  color: #666;
}

.otp-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.otp-inputs {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 1.5rem 0 2rem 0;
  width: 100%;
  max-width: 320px;
}

.otp-input {
  width: 3.5rem;
  height: 3.5rem;
  text-align: center;
  font-size: 2rem;
  border: 1.5px solid #ddd;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.otp-input:focus {
  border-color: #007a1f;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 122, 31, 0.1);
}

@media (max-width: 500px) {
  .otp-inputs {
    gap: 0.5rem;
    max-width: 220px;
  }
  .otp-input {
    width: 2.2rem;
    height: 2.2rem;
    font-size: 1.2rem;
  }
}

.otp-resend {
  text-align: center;
  margin-top: 1.5rem;
  color: #666;
  font-size: 0.95rem;
}

.otp-resend a {
  color: #007a1f;
  text-decoration: none;
  font-weight: 600;
  margin-left: 0.5rem;
  cursor: pointer;
}

.otp-resend a:hover {
  text-decoration: underline;
}

.otp-resend a:disabled {
  color: #999;
  cursor: not-allowed;
  text-decoration: none;
}

.otp-info {
  text-align: center;
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}
</style> 